import type { NewCapabilitySuggestion } from '@indutrust/capability-model';
import type { MachinePark } from '@indutrust/factory-model';
import { type } from '@ngrx/signals';
import { eventGroup } from '@ngrx/signals/events';

/**
 * Events driving the {@link import('./machine-park.store').MachineParkStore}.
 *
 * `loadRequested` fetches the flat machine list for a factory. Every mutation
 * re-emits `mutationSucceeded`, which triggers a reload. Adding a custom
 * (non-catalog) machine — i.e. `payload.machine_catalog_id` is unset — also
 * submits a `pending` entry to the shared machine catalog for admin review.
 * Catalog dual-write failures emit {@link catalogSubmitFailed} while the
 * factory save still succeeds; the next edit-save of a still-pending custom
 * machine retries automatically.
 *
 * Staged {@link NewCapabilitySuggestion} entries on `addRequested` /
 * `editRequested` are dual-written to `/capability_suggestions` in the same
 * save (soft-fail via {@link suggestionSubmitFailed}).
 *
 * Datasheet upload/delete are managed by the modal-scoped
 * {@link import('./machine-park-datasheet.store').MachineParkDatasheetStore}
 * via `withFileUpload()` — no events needed here.
 *
 * @example
 * const dispatch = injectDispatch(machineParkEvents);
 * dispatch.loadRequested({ factoryId: 7 });
 * dispatch.addRequested({ factoryId: 7, payload: { ... } });
 */
export const machineParkEvents = eventGroup({
  source: 'factory.machine-park',
  events: {
    loadRequested: type<{ factoryId: number }>(),
    loadSucceeded: type<{ factoryId: number; machines: MachinePark[] }>(),
    loadFailed: type<{ error: string }>(),

    addRequested: type<{
      factoryId: number;
      payload: Omit<MachinePark, 'id' | 'datasheets'>;
      capabilitySuggestions?: NewCapabilitySuggestion[];
    }>(),
    editRequested: type<{
      factoryId: number;
      machineId: number;
      payload: Omit<MachinePark, 'id' | 'datasheets'>;
      capabilitySuggestions?: NewCapabilitySuggestion[];
    }>(),
    deleteRequested: type<{
      factoryId: number;
      machineId: number;
    }>(),

    /** Soft-fail: factory machine saved but catalog POST failed. */
    catalogSubmitFailed: type<{ factoryId: number; machineId: number }>(),
    /** Soft-fail: factory machine saved but a capability suggestion POST failed. */
    suggestionSubmitFailed: type<{ factoryId: number; machineId: number }>(),

    mutationSucceeded: type<{ factoryId: number }>(),
    mutationFailed: type<{ error: string }>(),
  },
});
